<template>
    <section class="mobile-navigation">
        <div 
            @click="toggleMenu" 
            class="mobile-navigation__burger" 
            :class="{ active: isActive }">
            <span></span>
        </div>
        <nav class="mobile-navigation__items" :class="{ open: isOpen }">
            <ul>
                <li v-for="(link, index) in links" :key="index" @click="toggleMenu">
                    <router-link :to="link.route">{{ t(link.text) }}</router-link>
                </li>
            </ul>
            
            <div class="mobile-navigation__language">
                <button @click="locale = 'en'" :class="{ active: locale === 'en' }">EN</button>
                <button @click="locale = 'de'" :class="{ active: locale === 'de' }">DE</button>
            </div>
        </nav>
    </section>
</template>

<script>
import { useLang } from '@/composables/useLang';
import { gsap } from 'gsap';

export default {
    data() {
        return {
            isActive: false,
            isOpen: false,
            links: [
                { route: '/', text: 'navigation.home' },
                { route: '/about', text: 'navigation.about' },
                { route: '/playground', text: 'navigation.portfolio' },
                { route: '/contact', text: 'navigation.contact' },
            ],
        };
    },
    methods: {
        toggleMenu() {
            this.isActive = !this.isActive;
            this.isOpen = !this.isOpen;
            document.body.classList.toggle('no-scroll', this.isOpen);

            if (this.isOpen) {
                // Open animation
                gsap.set('.mobile-navigation__items', { visibility: 'visible' });
                gsap.to('.mobile-navigation__items', {
                    y: 0,
                    duration: 0.6,
                    ease: 'power3.out',
                });
                gsap.to('.mobile-navigation__items ul li', {
                    y: 0,
                    opacity: 1,
                    duration: 0.5,
                    stagger: 0.1,
                    ease: 'power2.out',
                    delay: 0.2,
                });
                gsap.to('.mobile-navigation__language', {
                    opacity: 1,
                    duration: 0.5,
                    ease: 'power2.out',
                    delay: 0.4,
                });
            } else {
                // Close animation
                gsap.to('.mobile-navigation__items ul li', {
                    y: '2rem',
                    opacity: 0,
                    duration: 0.4,
                    stagger: 0.05,
                    ease: 'power2.in',
                });
                gsap.to('.mobile-navigation__language', {
                    opacity: 0,
                    duration: 0.4,
                    ease: 'power2.in',
                });
                gsap.to('.mobile-navigation__items', {
                    y: '-100%',
                    duration: 0.6,
                    ease: 'power3.in',
                    onComplete: () => {
                        gsap.set('.mobile-navigation__items', { visibility: 'hidden' });
                    },
                });
            }
        }
    },
    setup() {
        const { t, locale } = useLang();
        return {
            t,        
            locale
        }
    },
    mounted() {
        // Initial state
        gsap.set('.mobile-navigation__items', { y: '-100%', visibility: 'hidden' });
        gsap.set('.mobile-navigation__items ul li', { y: '2rem', opacity: 0 });
        gsap.set('.mobile-navigation__language', { opacity: 0 });
    }
}
</script>

<style lang="scss" scoped>
.mobile-navigation {
    display: block;
    position: relative;
    &__burger {
        cursor: pointer;
        height: rem(25px);
        width: rem(35px);
        position: fixed;
        top: rem(35px);
        right: rem(20px);
        z-index: 1000;
        
        span {
            background-color: var(--color-black);
            display: block;
            position: absolute;
            height: rem(1px);
            width: 100%;
            top: 50%;
            transform: translateY(-50%);
            transition: background-color 0.3s;
            
            &::after, &::before {
                background-color: var(--color-black);
                content: '';
                display: block;
                height: rem(1px);
                width: 100%;
                position: absolute;
                transition: all 0.3s ease-in-out;
            }
            
            &::before {
                top: rem(-8px);
            }
            
            &::after {
                bottom: rem(-8px);
            }
        }
        
        &.active {
            span {
                background-color: transparent;
                
                &::before {
                    top: 0;
                    transform: rotate(45deg);
                    background-color: var(--color-white);
                }
                
                &::after {
                    bottom: 0;
                    transform: rotate(-45deg);
                    background-color: var(--color-white);
                }
            }
        }
    }
    
    &__items {
        background-color: var(--color-black);
        height: 100vh;
        width: 100vw;
        position: fixed;
        top: 0;
        left: 0;
        z-index: 999;
        display: flex;
        flex-direction: column;
        
        ul {
            list-style: none;
            display: flex;
            flex-direction: column;
            padding: rem(150px 32px 0 32px);
            margin: 0;
            
            li {
                margin-bottom: rem(20px);
                
                a {
                    font-family: var(--special-font);
                    font-size: rem(50px);
                    line-height: rem(60px);
                    color: var(--color-white);
                    text-decoration: none;
                    display: block;
                    
                    &:hover {
                        opacity: 0.8;
                    }
                }
            }
        }
        
        &.open {
            // Class kept for compatibility, but animation handled by GSAP
        }
    }
    
    &__language {
        display: flex;
        justify-content: center;
        gap: rem(15px);
        margin-top: auto;
        padding-bottom: rem(50px);
        
        button {
            background: none;
            border: 1px solid var(--color-white);
            color: var(--color-white);
            font-family: var(--special-font);
            font-size: rem(16px);
            padding: rem(8px 16px);
            cursor: pointer;
            transition: all 0.3s ease;
            
            &.active {
                background-color: var(--color-white);
                color: var(--color-black);
            }
            
            &:hover:not(.active) {
                background-color: rgba(255, 255, 255, 0.1);
            }
        }
    }
    
    @include desktop {
        display: none;
    }
}
</style>